import React, { useState, useEffect } from "react";
import { CFormSelect } from "@coreui/react";
import { CsrAPI } from "../apis/CsrAPI";
import { saveAs } from "file-saver";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import { toast } from "react-toastify";

const TrafficLightReportsDropdown = ({
  studyId,
  disabled = false,
  mainStatus = null,
  onReportsFetched = null,
}) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState("");

  // Fetch available reports on component mount
  useEffect(() => {
    if (studyId && !disabled) {
      fetchReports();
    }
  }, [studyId, disabled]);

  // Auto-refresh when mainStatus changes to ready (only once)
  useEffect(() => {
    if (mainStatus === "Ready for download" && studyId && !disabled) {
      fetchReports();
    }
  }, [mainStatus, studyId, disabled]);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await CsrAPI.getTrafficLightReports({ studyId });
      // Check if response exists and is an array before calling reverse
      const reportsList =
        response && Array.isArray(response) ? response.reverse() : [];
      setReports(reportsList);

      // Call the callback if provided
      if (onReportsFetched) {
        onReportsFetched(reportsList);
      }
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      console.error("Error fetching traffic light reports:", error);
      setError("Failed to load reports");

      if (!error?.response) {
        setError("No Server Response");
      } else if (error.response?.status === 401) {
        setError("Invalid user credentials");
      } else if (error.response?.status === 403) {
        setError("Access denied");
      } else {
        setError("Failed to load traffic light reports");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReportSelect = async event => {
    const reportName = event.target.value;
    if (!reportName) return;

    setSelectedReport(reportName);
    setLoading(true);

    try {
      const blob = await CsrAPI.downloadTrafficLightReport({
        studyId,
        reportName,
      });

      // Trigger file download
      saveAs(blob, reportName);
      toast.success("Report downloaded successfully!");
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      console.error("Error downloading report:", error);

      let errorMessage = "Failed to download report";
      if (!error?.response) {
        errorMessage = "No Server Response";
      } else if (error.response?.status === 401) {
        errorMessage = "Invalid user credentials";
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied";
      } else if (error.response?.status === 404) {
        errorMessage = "Report not found";
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setSelectedReport(""); // Reset selection
    }
  };

  // Don't render if disabled, but render disabled dropdown if reports array is empty
  if (disabled) {
    return null;
  }

  return (
    <div className="traffic-light-reports-dropdown">
      {/* TODO : I need to delete all the code related to excel sheet generation as the part of separate ticket */}
      {/* <div>selectedReport {selectedReport}</div> */}
      <CFormSelect
        className="input-select traffic-light-reports-select"
        value={selectedReport}
        onChange={handleReportSelect}
        disabled={loading || reports.length === 0}
        aria-label="History (Excel Report)">
        <option value="">
          {loading
            ? "Loading reports..."
            : reports.length === 0
              ? "No reports available"
              : "Select version"}
        </option>
        {reports.map((report, index) => (
          <option key={index} value={report}>
            {report}
          </option>
        ))}
      </CFormSelect>
      {error && (
        <div
          className="error-message"
          style={{
            color: "#dc3545",
            fontSize: "12px",
            marginTop: "4px",
          }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default TrafficLightReportsDropdown;
